/* eslint-disable */
/**
 *
 * FloatingDownloadTile
 *
 * @version 1.0.0
 * @since 12-Jun-2025
 */

import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Toggle from 'Hoc/ToggleComponent'
import AnimationIcon from '@nexidigital/animationicon'
import { Fab } from '@mui/material'
import './FloatingDownloadTile.scss'

const FloatingDownloadTile = ({
	iconDocument,
	title,
	subtitle,
	downloadIcon,
	loading,
	onClickDownload,
	processing,
}) => {
	const tileRef = useRef(null)
	const [isTextVisible, setIsTextVisible] = useState(true)
	const [isFocusEnabled, setIsFocusEnabled] = useState(false)
	const hasFocusedAfterProcessing = useRef(false)

	useEffect(() => {
		if (processing) {
			setIsTextVisible(false)
		}
		hasFocusedAfterProcessing.current = processing ? false : hasFocusedAfterProcessing.current
	}, [processing])

	useEffect(() => {
		const handleTabFocus = (event) => {
			if (event.key !== 'Tab') return
			if (processing) return
			if (hasFocusedAfterProcessing.current) return

			event.preventDefault()
			if (tileRef.current) {
				tileRef.current.focus({ preventScroll: true })
				setIsFocusEnabled(true)
				hasFocusedAfterProcessing.current = true
			}
		}

		window.addEventListener('keydown', handleTabFocus)

		return () => {
			window.removeEventListener('keydown', handleTabFocus)
		}
	}, [processing])

	useEffect(() => {
		if (!processing && tileRef.current) {
			tileRef.current.focus()
		}
	}, [processing])

	const handleKeyDown = e => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			onClickDownload()
		}
	}

	return (
		<Toggle>
			<Fab
				disableFocusRipple
				disableRipple
				className={`floatingDownloadTile ${isFocusEnabled ? 'focusEnabled' : 'focusDisabled'} ${processing ? 'collapsible' : ''} ${processing && isTextVisible ? 'open' : 'closed'
					}`}
				onClick={onClickDownload}
				onKeyDown={handleKeyDown}
				ref={tileRef}
				tabIndex={processing ? -1 : 0}>
				<div className='floatingDocumentIconContainer' role='presentation'>
					{iconDocument}
				</div>
				{isTextVisible && (
					<div className='floatingDownloadText'>
						<h1 className='floatingDownloadTitle'>{title}</h1>
						{subtitle && <h2 className='floatingDownloadSubtitle'>{subtitle}</h2>}
					</div>
				)}
				<div className='floatingDownloadIconContainer' role='presentation'>
					{loading ? <AnimationIcon status='loading' size='small' /> : downloadIcon}
				</div>
			</Fab>
		</Toggle>
	)
}

FloatingDownloadTile.propTypes = {
	// Add here some propTypes

	/** This prop is expected to be a node. It represents the icon of the document. */
	iconDocument: PropTypes.node.isRequired,
	/** This prop is expected to be a string. It represents the title of the document
	 * (the text will be cropped after the second row and an ellipsis will be added). */
	title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
	/** This prop is expected to be a string. It represents the subtitle of the document
	 * (the text will be cropped after the third row and an ellipsis will be added). */
	subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
	/** This prop is expected to be a node. It represents the icon of the download button. */
	downloadIcon: PropTypes.node,
	/** This prop is expected to be a function. It represents the function that will be executed on the download button click. */
	onClickDownload: PropTypes.func,
	/** This prop is expected to be a boolean. It represents the processing state of the file. */
	processing: PropTypes.bool,
	/** This prop is expected to be a boolean. It represents the loading state of download of the file. */
	loading: PropTypes.bool,
}

FloatingDownloadTile.defaultProps = {
	// Add here some default propTypes values
	title: 'Title',
	subtitle: '',
	downloadIcon: null,
	onClickDownload: () => { },
	loading: false,
	processing: false,
}

export default FloatingDownloadTile
